import { FC, useEffect, useRef } from "react";

import {
  Box,
  BoxProps,
  IconBox,
  IconBoxProps,
  Row,
  Text,
  Tooltip,
} from "@hightouch/htui";

import { ROW_HEIGHT } from "./constants";

type FilterPopoverTabProps = {
  isDisabled?: boolean;
  isFocused?: boolean;
  isSelected?: boolean;
  label: string;
  separator?: boolean;
  tip?: string;
  iconProps: IconBoxProps;
  onClick?: () => void;
};

export const FilterPopoverTab: FC<
  Readonly<FilterPopoverTabProps & BoxProps>
> = ({
  isFocused = false,
  isSelected = false,
  separator = false,
  label,
  tip,
  iconProps,
  ...props
}) => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isFocused) {
      ref.current?.focus();
    }
  }, [isFocused]);

  const focusBg = `${iconProps.color}.50`;
  const textColor = isSelected ? `${iconProps.color}.700` : "text.primary";

  return (
    <>
      <Tooltip isDisabled={!tip} message={tip} placement="left">
        <Row
          ref={ref}
          as="button"
          className="group"
          align="center"
          role="tab"
          bg={isSelected ? focusBg : "transparent"}
          height={ROW_HEIGHT}
          mx={2}
          p={2}
          gap={2}
          borderRadius="2xl"
          outline="none !important"
          borderColor="base.divider"
          transitionProperty="common"
          transitionDuration="fast"
          transitionTimingFunction="easeOutQuad"
          _hover={{
            bg: isSelected ? focusBg : "base.background",
          }}
          _focus={{ boxShadow: "inset 0 0 0 4px #E0F3F5" }}
          _active={{ bg: focusBg, boxShadow: "none" }}
          {...props}
        >
          <IconBox isSelected={isSelected} {...iconProps} />
          <Box
            as={Text}
            color={textColor}
            fontWeight="medium"
            transitionProperty="color"
            transitionDuration="fast"
            transitionTimingFunction="easeOutQuad"
            _groupHover={{ color: textColor }}
            textAlign="left"
            data-testid={`filter-popover-tab-label-${label.toLowerCase().replace(" ", "-")}`}
          >
            {label}
          </Box>
        </Row>
      </Tooltip>
      {separator && (
        <Box
          borderBottomWidth="hairline"
          borderBottomStyle="solid"
          borderColor="base.divider"
        />
      )}
    </>
  );
};
